import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, RefreshCw, AlertTriangle, Clock, Eye } from 'lucide-react';

const CATEGORY_LABELS = {
  ID: 'ID',
  SERVICE_AGREEMENT: 'Service Agreement',
  CONSENT: 'Consent',
  POLICY: 'Policy',
  HR: 'HR',
  INCIDENT_DOC: 'Incident',
  OTHER: 'Other',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function StatusBadgeFor({ type }) {
  if (type === 'EXPIRED') {
    return <Badge variant="destructive" className="text-[10px] font-normal" data-testid="doc-alert-badge-expired">Expired</Badge>;
  }
  if (type === 'EXPIRING_7D') {
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] font-normal" data-testid="doc-alert-badge-expiring">Expiring Soon</Badge>;
  }
  if (type === 'REVIEW_DUE') {
    return <Badge className="bg-sky-100 text-sky-700 border-sky-200 text-[10px] font-normal" data-testid="doc-alert-badge-review">Review Due</Badge>;
  }
  return null;
}

export default function DocumentAlertsSection() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/participants/documents/alert-summary');
      setData(res.data.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setData(null);
      } else {
        setError('Failed to load document alerts');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(); }, []);

  if (loading) {
    return (
      <Card data-testid="doc-alerts-loading">
        <CardContent className="py-6 flex items-center justify-center">
          <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground mr-2" />
          <span className="text-sm text-muted-foreground">Loading document alerts...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="doc-alerts-error">
        <CardContent className="py-6 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchAlerts} className="mt-2">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { expired_count = 0, expiring_7d_count = 0, expiring_30d_count = 0, review_due_count = 0, total_alerts = 0, items = [] } = data;

  return (
    <div className="space-y-4" data-testid="doc-alerts-section">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-base font-semibold">Document Alerts</h2>
        {total_alerts > 0 && (
          <Badge variant="destructive" className="text-[10px]">{total_alerts}</Badge>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="doc-alert-summary-cards">
        <Card className={expired_count > 0 ? 'border-red-300 bg-red-50/50' : ''}>
          <CardContent className="py-3 text-center">
            <p className={`text-xl font-bold ${expired_count > 0 ? 'text-red-600' : 'text-muted-foreground'}`} data-testid="doc-alert-expired-count">
              {expired_count}
            </p>
            <p className="text-[10px] text-muted-foreground">Expired</p>
          </CardContent>
        </Card>
        <Card className={expiring_7d_count > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
          <CardContent className="py-3 text-center">
            <p className={`text-xl font-bold ${expiring_7d_count > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} data-testid="doc-alert-expiring7d-count">
              {expiring_7d_count}
            </p>
            <p className="text-[10px] text-muted-foreground">Expiring (7 days)</p>
          </CardContent>
        </Card>
        <Card className={expiring_30d_count > 0 ? 'border-orange-300 bg-orange-50/50' : ''}>
          <CardContent className="py-3 text-center">
            <p className={`text-xl font-bold ${expiring_30d_count > 0 ? 'text-orange-600' : 'text-muted-foreground'}`} data-testid="doc-alert-expiring30d-count">
              {expiring_30d_count}
            </p>
            <p className="text-[10px] text-muted-foreground">Expiring (30 days)</p>
          </CardContent>
        </Card>
        <Card className={review_due_count > 0 ? 'border-sky-300 bg-sky-50/50' : ''}>
          <CardContent className="py-3 text-center">
            <p className={`text-xl font-bold ${review_due_count > 0 ? 'text-sky-600' : 'text-muted-foreground'}`} data-testid="doc-alert-review-count">
              {review_due_count}
            </p>
            <p className="text-[10px] text-muted-foreground">Review Due</p>
          </CardContent>
        </Card>
      </div>

      {/* Items Table */}
      {items && items.length > 0 ? (
        <Card>
          <div className="border rounded-lg overflow-hidden" data-testid="doc-alert-items-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs font-medium">Participant</TableHead>
                  <TableHead className="text-xs font-medium">Document</TableHead>
                  <TableHead className="text-xs font-medium w-[110px]">Type</TableHead>
                  <TableHead className="text-xs font-medium w-[110px]">Date</TableHead>
                  <TableHead className="text-xs font-medium w-[110px]">Status</TableHead>
                  <TableHead className="text-xs font-medium w-[70px] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={`${item.documentId}-${idx}`} data-testid={`doc-alert-row-${item.documentId}`}>
                    <TableCell className="text-sm font-medium">{item.participantName || '—'}</TableCell>
                    <TableCell className="text-sm">{item.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {CATEGORY_LABELS[item.category] || item.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(item.expiryDate || item.reviewDate)}
                    </TableCell>
                    <TableCell>
                      <StatusBadgeFor type={item.type} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => navigate(`/participants/${item.participantId}?tab=documents`)}
                        title="View in participant documents"
                        data-testid={`doc-alert-view-${item.documentId}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center" data-testid="doc-alerts-empty">
            <FileText className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No document alerts</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
